import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, TrendingUp, Wrench, CheckCircle, XCircle, AlertTriangle, FileText, Truck, Lock, Unlock, User, Building2, BarChart3, Clock, Activity } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import DockStatusBadge from '../components/DockStatusBadge';
import api from '../services/api';
import { authService } from '../services/authService';

const DockManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [selectedDock, setSelectedDock] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showMaterialReceiptModal, setShowMaterialReceiptModal] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showOverallAnalytics, setShowOverallAnalytics] = useState(false);
  const [selectedTruckForRecommendation, setSelectedTruckForRecommendation] = useState(null);
  const [selectedUnloadingOperation, setSelectedUnloadingOperation] = useState(null);
  const [poMaterials, setPoMaterials] = useState([]);
  const [materialReceipts, setMaterialReceipts] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [dockAnalytics, setDockAnalytics] = useState(null);
  const [overallAnalytics, setOverallAnalytics] = useState(null);
  const [assignForm, setAssignForm] = useState({
    truckId: '',
    poNumber: '',
    supplierId: ''
  });
  const [blockForm, setBlockForm] = useState({
    reason: '',
    duration: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  // Fetch docks
  const { data: docks = [], isLoading: docksLoading } = useQuery({
    queryKey: ['docks'],
    queryFn: async () => {
      const response = await api.get('/warehouse/docks/');
      return response.data.results || response.data;
    }
  });

  // Fetch trucks for dropdown
  const { data: trucks = [] } = useQuery({
    queryKey: ['trucks-for-dock'],
    queryFn: async () => {
      const response = await api.get('/gate/trucks/?status=DOCUMENTS_VERIFIED');
      return response.data.results || response.data;
    }
  });

  // Fetch suppliers for dropdown
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const response = await api.get('/procurement/suppliers/?is_active=true');
      return response.data.results || response.data;
    }
  });

  // Fetch purchase orders
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders-dock'],
    queryFn: async () => {
      const response = await api.get('/procurement/purchase-orders/?status=OPEN');
      return response.data.results || response.data;
    }
  });

  // Assign truck mutation
  const assignTruckMutation = useMutation({
    mutationFn: async ({ dockId, data }) => {
      const response = await api.post(`/warehouse/docks/${dockId}/assign_truck/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setShowAssignModal(false);
      setSelectedDock(null);
      setAssignForm({ truckId: '', poNumber: '', supplierId: '' });
      success('Truck assigned to dock successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to assign truck');
    }
  });

  // Release dock mutation
  const releaseDockMutation = useMutation({
    mutationFn: async (dockId) => {
      const response = await api.post(`/warehouse/docks/${dockId}/release/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSelectedDock(null);
      success('Dock released successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to release dock');
    }
  });

  // Block dock mutation
  const blockDockMutation = useMutation({
    mutationFn: async ({ dockId, data }) => {
      const response = await api.post(`/warehouse/docks/${dockId}/block/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setShowBlockModal(false);
      setSelectedDock(null);
      setBlockForm({ reason: '', duration: '' });
      success('Dock blocked successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to block dock');
    }
  });

  // Unblock dock mutation
  const unblockDockMutation = useMutation({
    mutationFn: async (dockId) => {
      const response = await api.post(`/warehouse/docks/${dockId}/unblock/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSelectedDock(null);
      success('Dock unblocked successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to unblock dock');
    }
  });

  // Start unloading mutation
  const startUnloadingMutation = useMutation({
    mutationFn: async (operationId) => {
      const response = await api.post(`/warehouse/unloading-operations/${operationId}/start/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSelectedDock(null);
      success('Unloading started successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to start unloading');
    }
  });

  // Complete unloading mutation
  const completeUnloadingMutation = useMutation({
    mutationFn: async (operationId) => {
      const response = await api.post(`/warehouse/unloading-operations/${operationId}/complete/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['docks']);
      setSelectedDock(null);
      success('Unloading completed successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to complete unloading');
    }
  });

  const handleAssignDock = (e) => {
    e.preventDefault();
    assignTruckMutation.mutate({
      dockId: selectedDock.id,
      data: {
        truck_id: assignForm.truckId,
        purchase_order_id: assignForm.poNumber || undefined,
        supplier_id: assignForm.supplierId
      }
    });
  };

  const handleBlockDock = (e) => {
    e.preventDefault();
    blockDockMutation.mutate({
      dockId: selectedDock.id,
      data: {
        reason: blockForm.reason,
        duration: blockForm.duration
      }
    });
  };

  // Get intelligent recommendations
  const handleGetRecommendations = async (truckId) => {
    try {
      const response = await api.post('/warehouse/docks/recommend_for_truck/', {
        truck_id: truckId,
        limit: 5
      });
      setRecommendations(response.data.recommendations || []);
      setSelectedTruckForRecommendation(truckId);
      setShowRecommendations(true);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to get recommendations');
    }
  };

  // Auto-assign dock
  const handleAutoAssign = async (truckId) => {
    try {
      const response = await api.post('/warehouse/docks/auto_assign/', {
        truck_id: truckId
      });
      queryClient.invalidateQueries(['docks']);
      success(response.data.message);
      setShowAssignModal(false);
      setSelectedDock(null);
      setAssignForm({ truckId: '', poNumber: '', supplierId: '' });
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to auto-assign dock');
    }
  };

  // Get dock analytics
  const handleViewAnalytics = async (dockId) => {
    try {
      const response = await api.get(`/warehouse/docks/${dockId}/analytics/?days=30`);
      setDockAnalytics(response.data);
      setShowAnalytics(true);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load analytics');
    }
  };

  // Get overall analytics
  const handleViewOverallAnalytics = async () => {
    try {
      const response = await api.get('/warehouse/docks/overall_analytics/?days=30');
      setOverallAnalytics(response.data);
      setShowOverallAnalytics(true);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to load overall analytics');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'AVAILABLE': return 'bg-green-100 text-green-800 border-green-300';
      case 'OCCUPIED': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'MAINTENANCE': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'BLOCKED': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'AVAILABLE': return <CheckCircle className="w-5 h-5" />;
      case 'OCCUPIED': return <Package className="w-5 h-5" />;
      case 'MAINTENANCE': return <Wrench className="w-5 h-5" />;
      case 'BLOCKED': return <Lock className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  // Filter docks
  const filteredDocks = docks.filter(dock => {
    if (filters.status !== 'all' && dock.status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        dock.dock_number?.toLowerCase().includes(search) ||
        dock.current_truck?.truck_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats = [
    { label: 'Total Docks', value: docks.length, color: 'bg-blue-500' },
    { label: 'Available', value: docks.filter(d => d.status === 'AVAILABLE').length, color: 'bg-green-500' },
    { label: 'Occupied', value: docks.filter(d => d.status === 'OCCUPIED').length, color: 'bg-purple-500' },
    { label: 'Blocked', value: docks.filter(d => d.status === 'BLOCKED' || d.status === 'MAINTENANCE').length, color: 'bg-red-500' },
  ];

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'AVAILABLE', label: 'Available' },
        { value: 'OCCUPIED', label: 'Occupied' },
        { value: 'MAINTENANCE', label: 'Maintenance' },
        { value: 'BLOCKED', label: 'Blocked' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dock Management</h1>
        <p className="text-gray-600 mt-2">Monitor and manage warehouse loading docks</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} text-white p-3 rounded-lg`}>
                <Package className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Overall Analytics Button */}
      <div className="flex justify-end">
        <button
          onClick={handleViewOverallAnalytics}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-purple-600 hover:to-indigo-700 flex items-center space-x-2 shadow-lg"
        >
          <BarChart3 className="w-5 h-5" />
          <span className="font-semibold">View Overall Dock Analytics</span>
        </button>
      </div>

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
        onClearFilters={() => setFilters({ search: '', status: 'all' })}
        searchValue={filters.search}
        searchPlaceholder="Search by dock number or truck..."
      />

      {/* Dock Grid */}
      {docksLoading ? (
        <LoadingState message="Loading docks..." />
      ) : filteredDocks.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No docks found"
          message={filters.search || filters.status !== 'all' ? "Try adjusting your filters" : "No docks available"}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocks.map((dock) => (
            <div
              key={dock.id}
              className={`card border-2 cursor-pointer hover:shadow-lg transition-shadow animate-fade-in ${getStatusColor(dock.status)}`}
              onClick={() => setSelectedDock(dock)}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">{dock.dock_number || `DOCK-${dock.id}`}</h3>
                  <p className="text-sm text-gray-600 mt-1">Capacity: {dock.capacity_tonnes}T</p>
                  {dock.dock_type_display && (
                    <p className="text-xs text-blue-600 font-medium mt-1">{dock.dock_type_display}</p>
                  )}
                  {dock.material_type_display && (
                    <p className="text-xs text-purple-600">{dock.material_type_display}</p>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(dock.status)}
                </div>
              </div>

              <div className="space-y-2">
                <DockStatusBadge detailedStatus={dock.detailed_status} size="md" showDescription={false} />

                {dock.current_truck && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <div className="flex items-center text-sm">
                      <Truck className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="font-bold text-blue-900">{dock.current_truck.truck_number}</span>
                    </div>
                    {dock.current_truck.supplier && (
                      <div className="flex items-center text-xs">
                        <Building2 className="w-3 h-3 mr-2 text-gray-500" />
                        <span className="text-gray-700">{dock.current_truck.supplier.name}</span>
                      </div>
                    )}
                    {dock.current_truck.time_elapsed && (
                      <div className="flex items-center text-xs">
                        <Clock className="w-3 h-3 mr-2 text-orange-500" />
                        <span className="text-orange-600 font-semibold">Elapsed: {dock.current_truck.time_elapsed}</span>
                      </div>
                    )}
                    {dock.current_truck.materials && dock.current_truck.materials.length > 0 && (
                      <div className="flex items-center text-xs">
                        <Package className="w-3 h-3 mr-2 text-purple-500" />
                        <span className="text-purple-600 font-medium">{dock.current_truck.materials.length} Material(s)</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center space-x-3 mt-3 text-xs">
                  <div className={`flex items-center ${dock.has_forklift ? 'text-green-600' : 'text-gray-400'}`}>
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Forklift
                  </div>
                  <div className={`flex items-center ${dock.has_weighbridge ? 'text-green-600' : 'text-gray-400'}`}>
                    <Package className="w-4 h-4 mr-1" />
                    Weighbridge
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dock Details Modal */}
      {selectedDock && !showAssignModal && !showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">{selectedDock.dock_number || `DOCK-${selectedDock.id}`}</h2>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <DockStatusBadge detailedStatus={selectedDock.detailed_status} size="md" showDescription={false} />
              </div>
              {selectedDock.detailed_status && (
                <div className="text-xs text-gray-600 italic pl-4">
                  {selectedDock.detailed_status.description}
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Capacity:</span>
                <span className="font-semibold">{selectedDock.capacity_tonnes} Tonnes</span>
              </div>

              <div className="pt-3 border-t">
                <h3 className="font-semibold mb-2">Facilities:</h3>
                <div className="flex justify-between">
                  <span className="text-gray-600">Forklift:</span>
                  <span className={selectedDock.has_forklift ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                    {selectedDock.has_forklift ? 'Available' : 'Not Available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Weighbridge:</span>
                  <span className={selectedDock.has_weighbridge ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                    {selectedDock.has_weighbridge ? 'Available' : 'Not Available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Loading Ramp:</span>
                  <span className={selectedDock.has_loading_ramp ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                    {selectedDock.has_loading_ramp ? 'Available' : 'Not Available'}
                  </span>
                </div>
              </div>

              {selectedDock.current_truck && (
                <div className="pt-3 border-t">
                  <h3 className="font-bold text-lg mb-3 text-blue-600">🚛 Current Truck Details</h3>

                  <div className="space-y-2 bg-blue-50 p-3 rounded-lg mb-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Truck Number:</span>
                      <span className="font-bold">{selectedDock.current_truck.truck_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gate Entry:</span>
                      <span className="font-semibold text-sm">{selectedDock.current_truck.gate_entry_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-semibold text-green-600">{selectedDock.current_truck.status}</span>
                    </div>
                    {selectedDock.current_truck.time_elapsed && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time Elapsed:</span>
                        <span className="font-bold text-orange-600">{selectedDock.current_truck.time_elapsed}</span>
                      </div>
                    )}
                  </div>

                  {selectedDock.current_truck.supplier && (
                    <div className="space-y-2 bg-purple-50 p-3 rounded-lg mb-3">
                      <h4 className="font-semibold text-purple-700">Supplier</h4>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-semibold">{selectedDock.current_truck.supplier.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Code:</span>
                        <span className="font-semibold">{selectedDock.current_truck.supplier.code}</span>
                      </div>
                    </div>
                  )}

                  {selectedDock.current_truck.driver && (
                    <div className="space-y-2 bg-green-50 p-3 rounded-lg mb-3">
                      <h4 className="font-semibold text-green-700">Driver</h4>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-semibold">{selectedDock.current_truck.driver.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">License:</span>
                        <span className="font-semibold">{selectedDock.current_truck.driver.license_number}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-semibold">{selectedDock.current_truck.driver.phone}</span>
                      </div>
                    </div>
                  )}

                  {selectedDock.current_truck.purchase_order && (
                    <div className="space-y-2 bg-yellow-50 p-3 rounded-lg mb-3">
                      <h4 className="font-semibold text-yellow-700">Purchase Order</h4>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">PO Number:</span>
                        <span className="font-bold">{selectedDock.current_truck.purchase_order.po_number}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-semibold">{selectedDock.current_truck.purchase_order.status}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Lines:</span>
                        <span className="font-semibold">{selectedDock.current_truck.purchase_order.total_lines}</span>
                      </div>
                    </div>
                  )}

                  {selectedDock.current_truck.unloading_operation && (
                    <div className="space-y-2 bg-indigo-50 p-3 rounded-lg mb-3">
                      <h4 className="font-semibold text-indigo-700">Unloading Operation</h4>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status:</span>
                        <span className="font-bold text-indigo-600">{selectedDock.current_truck.unloading_operation.status}</span>
                      </div>
                      {selectedDock.current_truck.unloading_operation.started_at && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Started At:</span>
                          <span className="font-semibold">{new Date(selectedDock.current_truck.unloading_operation.started_at).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {selectedDock.current_truck.unloading_operation.started_by && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Started By:</span>
                          <span className="font-semibold">{selectedDock.current_truck.unloading_operation.started_by}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedDock.current_truck.materials && selectedDock.current_truck.materials.length > 0 && (
                    <div className="space-y-2 bg-red-50 p-3 rounded-lg mb-3">
                      <h4 className="font-semibold text-red-700">Materials ({selectedDock.current_truck.materials.length})</h4>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {selectedDock.current_truck.materials.map((material, idx) => (
                          <div key={idx} className="bg-white p-2 rounded border border-red-200">
                            <div className="flex justify-between text-sm">
                              <span className="font-bold text-gray-800">{material.material_code}</span>
                              <span className="text-xs text-gray-600">{material.po_number}</span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">{material.material_name}</div>
                            <div className="grid grid-cols-2 gap-1 mt-2 text-xs">
                              <div>
                                <span className="text-gray-600">Ordered:</span>
                                <span className="font-semibold ml-1">{material.ordered_qty} {material.uom}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Received:</span>
                                <span className="font-semibold ml-1 text-green-600">{material.received_qty} {material.uom}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDock.current_truck.documents && (selectedDock.current_truck.documents.lr_number || selectedDock.current_truck.documents.invoice_number || selectedDock.current_truck.documents.eway_bill_number) && (
                    <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-gray-700">Documents</h4>
                      {selectedDock.current_truck.documents.lr_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">LR Number:</span>
                          <span className="font-semibold">{selectedDock.current_truck.documents.lr_number}</span>
                        </div>
                      )}
                      {selectedDock.current_truck.documents.invoice_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Invoice:</span>
                          <span className="font-semibold">{selectedDock.current_truck.documents.invoice_number}</span>
                        </div>
                      )}
                      {selectedDock.current_truck.documents.eway_bill_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">E-Way Bill:</span>
                          <span className="font-semibold">{selectedDock.current_truck.documents.eway_bill_number}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold mb-2">Actions:</h3>
              <div className="grid grid-cols-2 gap-2">
                {selectedDock.status === 'AVAILABLE' && (
                  <>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm col-span-2 flex items-center justify-center"
                    >
                      <Truck className="w-4 h-4 mr-2" />
                      Assign Truck
                    </button>
                    <button
                      onClick={() => setShowBlockModal(true)}
                      className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 text-sm flex items-center justify-center"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Block
                    </button>
                  </>
                )}

                {selectedDock.status === 'OCCUPIED' && (
                  <>
                    {/* Show Start Unloading if unloading operation exists but not started */}
                    {selectedDock.current_truck?.unloading_operation?.status === 'PENDING' && (
                      <button
                        onClick={() => navigate(`/material-receipt/${selectedDock.current_truck.unloading_operation.id}`)}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm col-span-2 flex items-center justify-center"
                      >
                        <Activity className="w-4 h-4 mr-2" />
                        Start Unloading & Receive Materials
                      </button>
                    )}

                    {/* Show Complete Unloading if unloading is in progress */}
                    {selectedDock.current_truck?.unloading_operation?.status === 'IN_PROGRESS' && (
                      <button
                        onClick={() => completeUnloadingMutation.mutate(selectedDock.current_truck.unloading_operation.id)}
                        className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 text-sm col-span-2 flex items-center justify-center"
                        disabled={completeUnloadingMutation.isLoading}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {completeUnloadingMutation.isLoading ? 'Completing...' : 'Complete Unloading'}
                      </button>
                    )}

                    {/* Show Release Dock only if unloading is completed */}
                    {selectedDock.current_truck?.unloading_operation?.status === 'COMPLETED' && (
                      <button
                        onClick={() => releaseDockMutation.mutate(selectedDock.id)}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm col-span-2 flex items-center justify-center"
                        disabled={releaseDockMutation.isLoading}
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        {releaseDockMutation.isLoading ? 'Releasing...' : 'Release Dock'}
                      </button>
                    )}

                    {/* Fallback: If no unloading operation, show release dock */}
                    {!selectedDock.current_truck?.unloading_operation && (
                      <button
                        onClick={() => releaseDockMutation.mutate(selectedDock.id)}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm col-span-2 flex items-center justify-center"
                        disabled={releaseDockMutation.isLoading}
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        {releaseDockMutation.isLoading ? 'Releasing...' : 'Release Dock'}
                      </button>
                    )}
                  </>
                )}

                {selectedDock.status === 'BLOCKED' && (
                  <button
                    onClick={() => unblockDockMutation.mutate(selectedDock.id)}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm col-span-2 flex items-center justify-center"
                    disabled={unblockDockMutation.isLoading}
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    {unblockDockMutation.isLoading ? 'Unblocking...' : 'Unblock Dock'}
                  </button>
                )}

                <button
                  onClick={() => handleViewAnalytics(selectedDock.id)}
                  className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 text-sm col-span-2 flex items-center justify-center"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Analytics
                </button>
              </div>

              <button
                onClick={() => setSelectedDock(null)}
                className="w-full mt-4 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Truck Modal */}
      {showAssignModal && selectedDock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">Assign Truck to {selectedDock.dock_number}</h2>

            <form onSubmit={handleAssignDock} className="space-y-4">
              <SmartDropdown
                label="Truck Number"
                name="truckId"
                value={assignForm.truckId}
                onChange={(e) => setAssignForm({...assignForm, truckId: e.target.value})}
                options={trucks.map(truck => ({
                  value: truck.id,
                  label: `${truck.truck_number} - ${truck.supplier?.name || 'Unknown'}`
                }))}
                icon={Truck}
                required
                placeholder="Select truck"
              />

              <SmartDropdown
                label="Supplier"
                name="supplierId"
                value={assignForm.supplierId}
                onChange={(e) => setAssignForm({...assignForm, supplierId: e.target.value})}
                options={suppliers.map(supplier => ({
                  value: supplier.id,
                  label: `${supplier.code} - ${supplier.name}`
                }))}
                icon={Building2}
                required
                placeholder="Select supplier"
              />

              <SmartDropdown
                label="PO Number (Optional)"
                name="poNumber"
                value={assignForm.poNumber}
                onChange={(e) => setAssignForm({...assignForm, poNumber: e.target.value})}
                options={purchaseOrders.map(po => ({
                  value: po.id,
                  label: `${po.po_number} - ${po.supplier?.name || ''}`
                }))}
                icon={FileText}
                placeholder="Select PO (optional)"
              />

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
                <p className="text-sm text-blue-800 font-medium mb-2">🤖 Smart Features:</p>
                <div className="flex flex-col space-y-2">
                  <button
                    type="button"
                    onClick={() => assignForm.truckId && handleGetRecommendations(assignForm.truckId)}
                    disabled={!assignForm.truckId}
                    className="bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600 text-sm disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Get Smart Recommendations
                  </button>
                  <button
                    type="button"
                    onClick={() => assignForm.truckId && handleAutoAssign(assignForm.truckId)}
                    disabled={!assignForm.truckId}
                    className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 text-sm disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Auto-Assign Best Dock
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignForm({ truckId: '', poNumber: '', supplierId: '' });
                  }}
                  className="btn-secondary"
                  disabled={assignTruckMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={assignTruckMutation.isLoading}
                >
                  {assignTruckMutation.isLoading ? 'Assigning...' : 'Assign Truck'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Block Dock Modal */}
      {showBlockModal && selectedDock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">Block {selectedDock.dock_number}</h2>

            <form onSubmit={handleBlockDock} className="space-y-4">
              <SmartDropdown
                label="Reason for Blocking"
                name="reason"
                value={blockForm.reason}
                onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})}
                options={[
                  { value: 'Reserved for JIT delivery', label: 'Reserved for JIT delivery' },
                  { value: 'Reserved for critical supplier', label: 'Reserved for critical supplier' },
                  { value: 'Scheduled for specific PO', label: 'Scheduled for specific PO' },
                  { value: 'Safety concerns', label: 'Safety concerns' },
                  { value: 'Equipment failure', label: 'Equipment failure' },
                  { value: 'Other', label: 'Other' }
                ]}
                icon={Lock}
                required
                placeholder="Select reason"
              />

              <SmartDropdown
                label="Expected Duration"
                name="duration"
                value={blockForm.duration}
                onChange={(e) => setBlockForm({...blockForm, duration: e.target.value})}
                options={[
                  { value: '1-2 hours', label: '1-2 hours' },
                  { value: '2-4 hours', label: '2-4 hours' },
                  { value: '4-8 hours', label: '4-8 hours' },
                  { value: 'Full day', label: 'Full day' },
                  { value: 'Multiple days', label: 'Multiple days' }
                ]}
                icon={AlertTriangle}
                placeholder="Select duration (optional)"
              />

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This dock will be unavailable until unblocked.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBlockModal(false);
                    setBlockForm({ reason: '', duration: '' });
                  }}
                  className="btn-secondary"
                  disabled={blockDockMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  disabled={blockDockMutation.isLoading}
                >
                  {blockDockMutation.isLoading ? 'Blocking...' : 'Block Dock'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Smart Recommendations Modal */}
      {showRecommendations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">🤖 Smart Dock Recommendations</h2>

            {recommendations.length === 0 ? (
              <p className="text-gray-600">No recommendations available</p>
            ) : (
              <div className="space-y-4">
                {recommendations.map((rec, index) => (
                  <div key={rec.dock_id} className={`border-2 rounded-lg p-4 ${
                    rec.score >= 80 ? 'border-green-300 bg-green-50' :
                    rec.score >= 60 ? 'border-blue-300 bg-blue-50' :
                    rec.score >= 40 ? 'border-yellow-300 bg-yellow-50' :
                    'border-red-300 bg-red-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-xl font-bold">{rec.dock_number}</h3>
                          <span className={`px-3 py-1 rounded text-sm font-bold ${
                            rec.score >= 80 ? 'bg-green-200 text-green-800' :
                            rec.score >= 60 ? 'bg-blue-200 text-blue-800' :
                            rec.score >= 40 ? 'bg-yellow-200 text-yellow-800' :
                            'bg-red-200 text-red-800'
                          }`}>
                            Score: {rec.score}/100
                          </span>
                          {index === 0 && rec.score >= 60 && (
                            <span className="bg-purple-500 text-white px-3 py-1 rounded text-xs font-bold animate-pulse">
                              BEST MATCH
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{rec.location}</p>
                        <p className="text-sm font-medium mt-2">{rec.recommendation}</p>

                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <div className="text-xs">
                            <span className="text-gray-600">Type:</span>
                            <span className="font-medium ml-1">{rec.dock_type}</span>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-600">Material:</span>
                            <span className="font-medium ml-1">{rec.material_type}</span>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-600">Status:</span>
                            <span className="font-medium ml-1">{rec.status}</span>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-600">Capacity:</span>
                            <span className="font-medium ml-1">{rec.facilities.capacity_tonnes}T</span>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Score Breakdown:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Availability: {rec.breakdown.availability}/30</div>
                            <div>Material Match: {rec.breakdown.material_compatibility}/25</div>
                            <div>Performance: {rec.breakdown.performance?.total || 0}/20</div>
                            <div>Capacity: {rec.breakdown.capacity_match}/15</div>
                            <div>Facilities: {rec.breakdown.facilities}/10</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                setShowRecommendations(false);
                setRecommendations([]);
              }}
              className="w-full mt-6 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && dockAnalytics && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">📊 {dockAnalytics.dock_number} Analytics</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Dock Type</p>
                  <p className="text-xl font-bold text-blue-600">{dockAnalytics.dock_type}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Material Type</p>
                  <p className="text-xl font-bold text-purple-600">{dockAnalytics.material_type}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Performance Metrics (Last {dockAnalytics.period_days} Days)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Utilization</p>
                    <p className="text-2xl font-bold text-green-600">
                      {dockAnalytics.metrics.utilization_percentage}%
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Avg Turnaround</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {dockAnalytics.metrics.average_turnaround_hours}h
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Trucks Handled</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {dockAnalytics.metrics.total_trucks_handled}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Downtime</p>
                    <p className="text-2xl font-bold text-red-600">
                      {dockAnalytics.metrics.downtime_hours}h
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Facilities</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`w-5 h-5 ${dockAnalytics.facilities.has_forklift ? 'text-green-600' : 'text-gray-400'}`} />
                    <span>Forklift</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`w-5 h-5 ${dockAnalytics.facilities.has_weighbridge ? 'text-green-600' : 'text-gray-400'}`} />
                    <span>Weighbridge</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`w-5 h-5 ${dockAnalytics.facilities.has_loading_ramp ? 'text-green-600' : 'text-gray-400'}`} />
                    <span>Loading Ramp</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    <span>{dockAnalytics.facilities.capacity_tonnes}T Capacity</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowAnalytics(false);
                setDockAnalytics(null);
              }}
              className="w-full mt-6 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Overall Analytics Modal */}
      {showOverallAnalytics && overallAnalytics && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold">📊 Overall Dock Analytics</h2>
              <span className="text-sm text-gray-600">Last {overallAnalytics.period_days} Days</span>
            </div>

            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Docks</p>
                      <p className="text-3xl font-bold text-blue-700 mt-1">{overallAnalytics.total_docks}</p>
                    </div>
                    <Package className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border-2 border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Avg Utilization</p>
                      <p className="text-3xl font-bold text-green-700 mt-1">{overallAnalytics.average_utilization}%</p>
                    </div>
                    <Activity className="w-10 h-10 text-green-500 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Total Trucks</p>
                      <p className="text-3xl font-bold text-purple-700 mt-1">{overallAnalytics.total_trucks_handled}</p>
                    </div>
                    <Truck className="w-10 h-10 text-purple-500 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-lg border-2 border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">Avg Turnaround</p>
                      <p className="text-3xl font-bold text-yellow-700 mt-1">{overallAnalytics.average_turnaround}h</p>
                    </div>
                    <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
                  </div>
                </div>
              </div>

              {/* Dock Performance Table */}
              <div className="border-t pt-6">
                <h3 className="text-xl font-bold mb-4">Individual Dock Performance</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilization</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Turnaround</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trucks</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Downtime</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {overallAnalytics.dock_performances?.map((dock) => (
                        <tr key={dock.dock_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{dock.dock_number}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{dock.dock_type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{dock.material_type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-sm font-semibold text-gray-900">{dock.utilization_percentage}%</div>
                              <div className="ml-2 w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    dock.utilization_percentage >= 80 ? 'bg-green-500' :
                                    dock.utilization_percentage >= 60 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(dock.utilization_percentage, 100)}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{dock.average_turnaround_hours}h</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{dock.total_trucks_handled}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-red-600 font-medium">{dock.downtime_hours}h</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Workload Distribution */}
              {overallAnalytics.workload_distribution && (
                <div className="border-t pt-6">
                  <h3 className="text-xl font-bold mb-4">Workload Distribution</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {overallAnalytics.workload_distribution.map((item) => (
                      <div key={item.dock_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{item.dock_number}</h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.current_status === 'AVAILABLE' ? 'bg-green-100 text-green-800' :
                            item.current_status === 'OCCUPIED' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.current_status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Active Operations:</span>
                            <span className="font-semibold">{item.active_operations}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Pending Tasks:</span>
                            <span className="font-semibold">{item.pending_count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowOverallAnalytics(false);
                setOverallAnalytics(null);
              }}
              className="w-full mt-6 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DockManagement;
